import type { ReadonlyURLSearchParams } from "next/navigation";

const MAX_VALUE_LENGTH = 160;

const sanitize = (value: string | null | undefined) => {
  if (!value) return undefined;
  const normalized = value.trim().replace(/[\u0000-\u001f\u007f]/g, "");
  return normalized ? normalized.slice(0, MAX_VALUE_LENGTH) : undefined;
};

const getStoredMetaAttribution = () => {
  if (typeof document === "undefined") return {};
  const raw = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith("sr_meta_attribution="));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(decodeURIComponent(raw.slice("sr_meta_attribution=".length))) as Record<
      string,
      unknown
    >;
    return Object.fromEntries(
      [
        "session_id",
        "landing_page",
        "fbclid",
        "fbc",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
      ].flatMap((key) => {
        const value = sanitize(typeof parsed[key] === "string" ? parsed[key] : undefined);
        return value ? [[key, value]] : [];
      })
    );
  } catch {
    return {};
  }
};

export function getUtmTrackingParameters(searchParams: ReadonlyURLSearchParams | null) {
  const stored = getStoredMetaAttribution();
  const params = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].reduce<
    Record<string, string | undefined>
  >((result, key) => {
    result[key] = sanitize(searchParams?.get(key)) || (stored[key] as string | undefined);
    return result;
  }, {});
  const cookieValue = (name: string) => {
    if (typeof document === "undefined") return undefined;
    const raw = document.cookie
      .split(";")
      .map((value) => value.trim())
      .find((value) => value.startsWith(`${name}=`));
    return raw ? sanitize(decodeURIComponent(raw.slice(name.length + 1))) : undefined;
  };
  const tracking = {
    ...stored,
    ...params,
    fbp: cookieValue("_fbp"),
    fbc: cookieValue("_fbc") || (stored.fbc as string | undefined),
  };

  return Object.values(tracking).every((value) => value === undefined) ? undefined : tracking;
}
